import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserAddOutlined } from "@ant-design/icons";
import { Alert, App, Button, Input, Spin, Table, Tag, Tooltip } from "antd";
import { observer } from "mobx-react-lite";

import {
  inviteTeamUserByEmail,
  type OutgoingTeamRequest,
  TeamInvitationError,
  type TeamMember,
} from "../../api/team";
import { teamStore } from "../../stores/teamStore";
import { userStore } from "../../stores/userStore";
import { formatDate } from "../../utils/date";
import { usePendingAction } from "../../utils/usePendingAction";
import { TeamAddMemberModal } from "./TeamAddMemberModal";
import { TeamSizeAlertModal } from "./TeamSizeAlertModal";

type DisplayMember = TeamMember & { isOwner?: boolean };

export const OwnTeamTab = observer(function OwnTeamTab() {
  const { t, i18n } = useTranslation();
  const { modal, notification } = App.useApp();
  const plan = userStore.subscription?.plan ?? "lemon";
  const username = userStore.profile?.username ?? "";
  const team = teamStore.ownTeam;

  const storeReady = userStore.subscription !== null;
  const loading = !storeReady || teamStore.ownTeamLoading;

  const effectiveTeam = team;

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [teamSizeAlertOpen, setTeamSizeAlertOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const [cancellingInvitation, setCancellingInvitation] = useState<
    string | null
  >(null);
  const { run: runCancelInvitation } = usePendingAction();
  const { run: runRemoveMember } = usePendingAction();
  const pendingInvitations = teamStore.outgoingInvitations;

  const teamSize = effectiveTeam?.maximum_size ?? 0;
  const allMembers: DisplayMember[] = effectiveTeam
    ? [
        {
          nextgis_guid: effectiveTeam.owner_id ?? "team-owner",
          username: `${effectiveTeam.owner} (${t("team.you")})`,
          email: userStore.profile?.email ?? "",
          isOwner: true,
        },
        ...effectiveTeam.users,
      ]
    : [];

  const filteredMembers = tableSearch
    ? allMembers.filter((m) => {
        const search = tableSearch.toLowerCase();
        return (
          m.username.toLowerCase().includes(search) ||
          m.email?.toLowerCase().includes(search)
        );
      })
    : allMembers;

  const teamPlacesTaken = allMembers.length + pendingInvitations.length;
  const teamLimitReached = teamPlacesTaken >= teamSize;
  const addDisabled = teamStore.outgoingInvitationsLoading || teamLimitReached;
  const addTooltip = teamLimitReached
    ? t("team.addDisabledLimitTooltip")
    : undefined;

  const renderInvitationStatus = (invitation: OutgoingTeamRequest) => {
    const status =
      invitation.invitee_status === "account_exists"
        ? "awaiting"
        : "unregistered";
    return (
      <Tag
        className={`team-manage-page__invitation-status team-manage-page__invitation-status--${status}`}
      >
        <span
          className="team-manage-page__invitation-status-dot"
          aria-hidden="true"
        />
        {t(
          invitation.invitee_status === "account_exists"
            ? "team.awaitingResponse"
            : "team.notRegisteredYet",
        )}
      </Tag>
    );
  };

  const handleAddClick = () => {
    if (!addDisabled) {
      setAddDialogOpen(true);
    } else {
      setTeamSizeAlertOpen(true);
    }
  };

  const handleEmailInvite = async (email: string) => {
    try {
      await inviteTeamUserByEmail(email);
      await teamStore.loadOutgoingInvitations();
      notification.success({ title: t("team.invitationSent") });
    } catch (error) {
      const errorKey =
        error instanceof TeamInvitationError
          ? {
              already_invited: "team.alreadyInvited",
              same_email: "team.cannotInviteYourself",
            }[error.code]
          : undefined;
      notification.error({
        title: errorKey ? t(errorKey) : t("errors.shared.common_error"),
      });
      throw error;
    }
  };

  const handleCancelInvitation = (invitation: OutgoingTeamRequest) => {
    modal.confirm({
      className: "team-manage-page__cancel-invitation-modal",
      title: t("team.cancelInvitationConfirm", {
        email: invitation.email,
      }),
      okText: t("team.cancel"),
      cancelText: t("app.goBack"),
      okButtonProps: { danger: true },
      onOk: () =>
        runCancelInvitation(async () => {
          setCancellingInvitation(invitation.guid);
          try {
            await teamStore.cancelOutgoingInvitation(invitation.guid);
          } catch (error) {
            notification.error({ title: t("errors.shared.common_error") });
            throw error;
          } finally {
            setCancellingInvitation(null);
          }
        }),
    });
  };

  const handleRemove = (user: DisplayMember) => {
    if (!effectiveTeam) return;
    modal.confirm({
      title: t("team.deleteUserConfirm", { email: user.email }),
      okText: t("team.remove"),
      cancelText: t("team.cancel"),
      onOk: () =>
        runRemoveMember(async () => {
          setRemoving(user.nextgis_guid);
          try {
            await teamStore.updateMembers(
              effectiveTeam.users.filter(
                (u) => u.nextgis_guid !== user.nextgis_guid,
              ),
            );
          } finally {
            setRemoving(null);
          }
        }),
    });
  };

  if (loading) {
    return (
      <div className="team-manage-page__center">
        <Spin size="large" />
      </div>
    );
  }

  if (plan !== "pomelo") {
    return (
      <Alert
        type="info"
        className="team-manage-page__upgrade-alert"
        description={
          <Trans
            i18nKey="team.teamIsNotAvailable"
            components={{
              lnk: (
                <Link to="/subscription/update/manage?plan=pomelo&period=monthly" />
              ),
            }}
          />
        }
        showIcon
      />
    );
  }

  return (
    <div className="team-manage-page__own-team">
      <div className="team-manage-page__own-team-header">
        <h2>{t("team.teamOf", { owner: username })}</h2>
        {teamSize > 0 && (
          <Tag color={allMembers.length <= teamSize ? "blue" : "error"}>
            {t("team.teamCount", { count: allMembers.length, max: teamSize })}
          </Tag>
        )}
        {allMembers.length <= 10 && (
          <Tooltip title={addTooltip}>
            <Button
              type="primary"
              size="large"
              icon={<UserAddOutlined />}
              onClick={handleAddClick}
              disabled={addDisabled}
              style={{ marginLeft: "auto" }}
            >
              {t("team.add")}
            </Button>
          </Tooltip>
        )}
      </div>

      {effectiveTeam && allMembers.length > teamSize && (
        <Alert
          type="error"
          className="team-manage-page__limit-exceeded"
          description={
            <Trans
              i18nKey="team.limitExceeded"
              values={{ count: allMembers.length - teamSize }}
              components={{
                strong: <strong />,
                chatLink: <Link to="/support" />,
              }}
            />
          }
          showIcon
        />
      )}

      {allMembers.length > 10 && (
        <div className="team-manage-page__toolbar">
          <Input
            size="large"
            placeholder={t("team.search")}
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            className="team-manage-page__search-input"
          />
          <Tooltip title={addTooltip}>
            <Button
              type="primary"
              size="large"
              icon={<UserAddOutlined />}
              onClick={handleAddClick}
              disabled={addDisabled}
            >
              {t("team.add")}
            </Button>
          </Tooltip>
        </div>
      )}

      <Table<DisplayMember>
        className="team-manage-page__table team-manage-page__members-table"
        dataSource={filteredMembers}
        rowKey="nextgis_guid"
        pagination={allMembers.length > 10 ? undefined : false}
        columns={[
          {
            title: t("team.username"),
            key: "username",
            render: (_: unknown, member: DisplayMember) => (
              <div className="team-manage-page__invitee">
                <span>{member.username}</span>
                {member.email && (
                  <span className="team-manage-page__invitee-email">
                    {member.email}
                  </span>
                )}
                {member.dt_joined && (
                  <span className="team-manage-page__mobile-detail">
                    {t("team.joined")}:{" "}
                    {formatDate(member.dt_joined, i18n.language)}
                  </span>
                )}
              </div>
            ),
          },
          {
            title: t("team.joined"),
            dataIndex: "dt_joined",
            key: "dt_joined",
            className: "team-manage-page__desktop-column",
            render: (value: string | null | undefined) =>
              value ? formatDate(value, i18n.language) : "—",
          },
          {
            key: "action",
            align: "right" as const,
            className: "team-manage-page__action-column",
            render: (_: unknown, record: DisplayMember) =>
              !record.isOwner ? (
                <Button
                  loading={removing === record.nextgis_guid}
                  disabled={
                    removing !== null && removing !== record.nextgis_guid
                  }
                  onClick={() => handleRemove(record)}
                >
                  {t("team.remove")}
                </Button>
              ) : null,
          },
        ]}
      />

      {(teamStore.outgoingInvitationsLoading ||
        pendingInvitations.length > 0) && (
        <section className="team-manage-page__pending-invitations">
          <h3 className="team-manage-page__pending-invitations-title">
            {t("team.sentInvitations")}
          </h3>
          <Table<OutgoingTeamRequest>
            className="team-manage-page__table"
            dataSource={pendingInvitations}
            rowKey="guid"
            loading={teamStore.outgoingInvitationsLoading}
            pagination={pendingInvitations.length > 10 ? undefined : false}
            columns={[
              {
                title: t("team.invitedEmailUsername"),
                key: "invitee",
                render: (_: unknown, invitation: OutgoingTeamRequest) => (
                  <div className="team-manage-page__invitee">
                    <span>{invitation.invitee_username ?? "—"}</span>
                    <span className="team-manage-page__invitee-email">
                      {invitation.email}
                    </span>
                    <span className="team-manage-page__mobile-detail">
                      {renderInvitationStatus(invitation)}
                    </span>
                  </div>
                ),
              },
              {
                title: t("team.status"),
                key: "status",
                className: "team-manage-page__desktop-column",
                render: (_: unknown, invitation: OutgoingTeamRequest) =>
                  renderInvitationStatus(invitation),
              },
              {
                key: "action",
                align: "right" as const,
                className: "team-manage-page__action-column",
                render: (_: unknown, invitation: OutgoingTeamRequest) => (
                  <Button
                    loading={cancellingInvitation === invitation.guid}
                    disabled={
                      cancellingInvitation !== null &&
                      cancellingInvitation !== invitation.guid
                    }
                    onClick={() => handleCancelInvitation(invitation)}
                  >
                    {t("team.cancel")}
                  </Button>
                ),
              },
            ]}
          />
        </section>
      )}

      <TeamAddMemberModal
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onInviteByEmail={handleEmailInvite}
      />

      <TeamSizeAlertModal
        open={teamSizeAlertOpen}
        onClose={() => setTeamSizeAlertOpen(false)}
      />
    </div>
  );
});
